// CollectionChips — крупные droppable-плитки коллекций внутри «Дока действий».
//
// Каждая плитка = @dnd-kit useDroppable; при наведении перетаскиваемой карточки
// заливается ЦВЕТОМ коллекции + кольцо/glow + haptic.selection() (mobile). Цели
// крупные (122×84) — легко попасть пальцем/курсором. Порядок: недавно-
// использованные (lastUsedSetIds) → по updatedAt; топ-5. При >5 коллекциях —
// плитка «Выбрать коллекцию…» (drop → searchable-пикер со всеми). Плюс плитка
// «Новая коллекция» (drop → create по названию элемента).
//
// Источник — useMaterialSets() (PowerSync, реактивно). Цвет — set.color ?? дефолт.
// Док виден только во время drag → все взаимодействия здесь — это drop'ы.

import { useDroppable } from "@dnd-kit/core";
import { useHaptic } from "@studyhub/platform/hooks";
import { Skeleton } from "@studyhub/ui/components/skeleton";
import { IconFolderFilled, IconFolderPlus, IconSearch, IconStack2 } from "@tabler/icons-react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMaterialSets } from "@/components/materials/_api";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/cn";
import { IS_MOBILE_PLATFORM } from "@/lib/platform-detect";
import { useDragSessionStore } from "@/stores/drag-session";
import { NEW_COLLECTION_DROP_ID, PICK_COLLECTION_DROP_ID } from "./use-collection-drop";
import { useDockEdgeAutoscroll } from "./use-dock-edge-autoscroll";

/** Тактильный «тик» при наведении карточки на droppable-плитку (только mobile). */
function useHoverHaptic(isOver: boolean): void {
	const haptic = useHaptic();
	const prev = useRef(false);
	useEffect(() => {
		if (isOver && !prev.current && IS_MOBILE_PLATFORM) haptic.selection();
		prev.current = isOver;
	}, [isOver, haptic]);
}

/** Дефолтный цвет плитки, когда у коллекции NULL color (indigo). */
const DEFAULT_CHIP_COLOR = "#6366F1";
/** Сколько коллекций показываем плитками; остальное — за «Выбрать коллекцию…». */
const VISIBLE_LIMIT = 5;
/** Общая геометрия плитки — крупная цель, легко попасть. */
const TILE_BASE =
	"relative flex h-[84px] w-[122px] shrink-0 flex-col justify-between rounded-[var(--radius-card)] border p-2.5 text-left";

interface ChipSet {
	id: string;
	name: string;
	color: string;
	count: number;
	updatedAt: string;
}

export function CollectionChips(): React.ReactElement {
	const { t } = useTranslation("library");
	const setsQuery = useMaterialSets();
	const lastUsedSetIds = useDragSessionStore((s) => s.lastUsedSetIds);
	// Edge-автоскролл ряда чипов во время drag — чтобы дотянуть карточку до дальних
	// коллекций, не отпуская палец (mobile). На desktop безвреден.
	const scrollRef = useRef<HTMLDivElement>(null);
	useDockEdgeAutoscroll(scrollRef);

	// Порядок: недавно-использованные (в их порядке) → остальные по updatedAt desc.
	const ordered = useMemo<ChipSet[]>(() => {
		const items = (setsQuery.data?.items ?? []).map((s) => ({
			id: s.id,
			name: s.name,
			color: s.color ?? DEFAULT_CHIP_COLOR,
			count: s.materialsCount,
			updatedAt: s.updatedAt,
		}));
		const byId = new Map(items.map((s) => [s.id, s]));
		const recent: ChipSet[] = [];
		for (const id of lastUsedSetIds) {
			const found = byId.get(id);
			if (found) {
				recent.push(found);
				byId.delete(id);
			}
		}
		const rest = [...byId.values()].toSorted((a, b) => b.updatedAt.localeCompare(a.updatedAt));
		return [...recent, ...rest];
	}, [setsQuery.data?.items, lastUsedSetIds]);

	// Loading: первый снапшот SQLite ещё не пришёл — скелетоны (ряд не «прыгает»).
	if (setsQuery.isLoading && ordered.length === 0) {
		return <TilesSkeleton />;
	}

	// Empty: коллекций нет — только «Новая коллекция» + подсказка.
	if (ordered.length === 0) {
		return (
			<div className="flex items-center gap-2.5">
				<NewTile />
				<span className="text-xs text-muted-foreground">{t("dock.emptyHint")}</span>
			</div>
		);
	}

	const visible = ordered.slice(0, VISIBLE_LIMIT);
	const hasOverflow = ordered.length > VISIBLE_LIMIT;

	return (
		<div
			ref={scrollRef}
			className="flex items-stretch gap-2.5 overflow-x-auto overscroll-x-contain pb-1"
		>
			{visible.map((s) => (
				<CollectionTile key={s.id} id={s.id} name={s.name} color={s.color} count={s.count} />
			))}
			{hasOverflow && <PickTile />}
			<NewTile />
		</div>
	);
}

function TilesSkeleton(): React.ReactElement {
	return (
		<div className="flex gap-2.5" aria-hidden>
			{[0, 1, 2, 3].map((i) => (
				<Skeleton key={i} className="h-[84px] w-[122px] rounded-[var(--radius-card)]" />
			))}
		</div>
	);
}

// ───────────────────────── Плитка коллекции ─────────────────────────

function CollectionTile({
	id,
	name,
	color,
	count,
}: {
	id: string;
	name: string;
	color: string;
	count: number;
}): React.ReactElement {
	const reducedMotion = usePrefersReducedMotion();
	const { setNodeRef, isOver } = useDroppable({ id, data: { setId: id } });
	useHoverHaptic(isOver);

	return (
		<div
			ref={setNodeRef}
			data-set-chip={id}
			data-drop-over={isOver ? "" : undefined}
			// При наведении карточки заливаем плитку ЦВЕТОМ коллекции + ВНУТРЕННЕЕ кольцо
			// и свечение (inset — не вылезает за overflow-x скролл-ряда, рамка не режется).
			// hex+alpha работает для 6-знач. set.color.
			style={
				isOver
					? {
							borderColor: color,
							backgroundColor: `${color}26`,
							boxShadow: `inset 0 0 0 2px ${color}, inset 0 0 18px ${color}40`,
						}
					: undefined
			}
			className={cn(
				TILE_BASE,
				"border-border bg-card/80",
				!reducedMotion && "transition-[background-color,border-color,box-shadow] duration-fast",
				!isOver && "hover:border-foreground/20",
			)}
		>
			<span
				className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-md)]"
				style={{ backgroundColor: `${color}1f`, color }}
			>
				<IconFolderFilled size={18} stroke={1.75} />
			</span>
			<span className="min-w-0">
				<span className="line-clamp-1 text-sm font-semibold leading-tight text-foreground">
					{name}
				</span>
				<span className="mt-0.5 flex items-center gap-1 text-2xs tabular-nums text-muted-foreground">
					<IconStack2 size={11} stroke={1.75} />
					{count}
				</span>
			</span>
		</div>
	);
}

// ───────────────────────── «Выбрать коллекцию…» ─────────────────────────

function PickTile(): React.ReactElement {
	const { t } = useTranslation("library");
	const reducedMotion = usePrefersReducedMotion();
	const { setNodeRef, isOver } = useDroppable({
		id: PICK_COLLECTION_DROP_ID,
		data: { setId: PICK_COLLECTION_DROP_ID },
	});
	useHoverHaptic(isOver);

	return (
		<div
			ref={setNodeRef}
			data-set-chip={PICK_COLLECTION_DROP_ID}
			data-drop-over={isOver ? "" : undefined}
			className={cn(
				TILE_BASE,
				!reducedMotion && "transition-[background-color,border-color,box-shadow] duration-fast",
				isOver
					? "border-primary bg-primary/15 ring-2 ring-inset ring-primary/60"
					: "border-border bg-card/80 hover:border-primary/40",
			)}
		>
			<span className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-primary/10 text-primary">
				<IconSearch size={18} stroke={1.75} />
			</span>
			<span className="line-clamp-2 text-sm font-semibold leading-tight text-foreground">
				{t("dock.pick")}
			</span>
		</div>
	);
}

// ───────────────────────── «Новая коллекция» ─────────────────────────

function NewTile(): React.ReactElement {
	const { t } = useTranslation("library");
	const reducedMotion = usePrefersReducedMotion();
	const { setNodeRef, isOver } = useDroppable({
		id: NEW_COLLECTION_DROP_ID,
		data: { setId: NEW_COLLECTION_DROP_ID },
	});
	useHoverHaptic(isOver);

	return (
		<div
			ref={setNodeRef}
			data-set-chip={NEW_COLLECTION_DROP_ID}
			data-drop-over={isOver ? "" : undefined}
			className={cn(
				TILE_BASE,
				"border-dashed",
				!reducedMotion && "transition-[background-color,border-color,box-shadow] duration-fast",
				isOver
					? "border-primary bg-primary/15 ring-2 ring-inset ring-primary/60"
					: "border-border/80 bg-card/40 hover:border-primary/40",
			)}
		>
			<span className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-muted text-muted-foreground">
				<IconFolderPlus size={18} stroke={1.75} />
			</span>
			<span className="line-clamp-2 text-sm font-semibold leading-tight text-muted-foreground">
				{t("dock.newCollection")}
			</span>
		</div>
	);
}
